import React, { useEffect } from 'react';
import {
  BrowserRouter as Router, Routes, Route, Navigate,
} from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { ToastContainer, toast } from 'react-toastify';
import { Meteor } from 'meteor/meteor';
import { isMobile, isPublicPath, queryStringToObject } from '/imports/utils';
import { useFindOne, useLoggedOutRedirect, useLoggingIn, useSubscribe, useUser } from '/imports/hooks';
import { strings } from '/imports/strings';
import { registerSubscription } from '/imports/subscriptions';
import { sendEmailVerification, setOrgId } from '/imports/api/users';
import { RedirectionPage } from '/imports/ui/RedirectionPage';
import { Publications } from '/imports/publications';
import { AppStyled } from '/imports/ui/AppStyled';
import { LoginPage } from '/imports/ui/LoginPage';
import { ForgotPasswordPage } from '/imports/ui/ForgotPasswordPage';
import { AcceptInvitationPage } from '/imports/ui/AcceptInvitationPage';
import { ResetPasswordPage } from '/imports/ui/ResetPasswordPage';
import { CommonRoutes } from '/imports/ui/CommonRoutes';
import { landingPathFor, Nav } from '/imports/ui/Nav';
import { MainContent } from '/imports/ui/MainContent';
import { AppHeader } from '/imports/ui/AppHeader';
import { SignupPage } from '/imports/ui/SignupPage';
import { SignupSubmittedPage } from '/imports/ui/SignupSubmittedPage';
import { VerifyEmailPage } from '/imports/ui/VerifyEmailPage';
import { Orgs } from '/imports/api/orgs';
import { Account, Accounts } from '/imports/api/accounts';
import { TermsPage } from '/imports/ui/TermsPage';
import { PrivacyPolicyPage } from '/imports/ui/PrivacyPolicyPage';
import { FooterNav } from '/imports/ui/FooterNav';
import { ImpersonationBanner } from '/imports/ui/ImpersonationBanner';
import { LoadingFullScreen } from '/imports/ui/LoadingFullScreen';
import { GoogleOauth2RedirectPage } from '/imports/ui/GoogleOauth2RedirectPage';
import { DiscordOauth2RedirectPage } from '/imports/ui/DiscordOauth2Redirect';
import { Oauth2ThankYouPage } from '/imports/ui/Oauth2ThankYouPage';
import { MessagesPage } from '/imports/ui/MessagesPage';
import { EnvironmentPage } from '/imports/ui/EnvironmentPage';
import { DevToolsPage } from '/imports/ui/DevToolsPage';
import { QuickbooksConnectedPage } from '/imports/ui/QuickbooksConnectedPage';
import { ManagePage } from '/imports/ui/ManagePage';
import { ProjectsPage } from '/imports/ui/ProjectsPage';
import { ResiGenPage } from '/imports/ui/ResiGenPage';
import { ResiGenProjectViewPage } from '/imports/ui/ResiGenProjectViewPage';
import { ProjectPage } from '/imports/ui/ProjectPage';
import { ProjectViewPage } from '/imports/ui/ProjectViewPage';
import { ProductionMetersPage } from '/imports/ui/ProductionMetersPage';
import { ProductionMeterReportPage } from '/imports/ui/ProductionMeterReportPage';
import { EgaugeMeterPage } from '/imports/ui/EgaugeMeterPage';
import { EnphaseMeterPage } from '/imports/ui/EnphaseMeterPage';
import { PowerhubMeterPage } from '/imports/ui/PowerhubMeterPage';
import { FomwareMeterPage } from '/imports/ui/FomwareMeterPage';
import { ProductionMeterPage } from '/imports/ui/ProductionMeterPage';
import { SolarBillsPage } from '/imports/ui/SolarBillsPage';
import { NewSolarBillsPage } from '/imports/ui/NewSolarBillsPage';
import { RealtyIncomeBillsPage } from '/imports/ui/RealtyIncomeBillsPage';
import { PrimeRatePage } from '/imports/ui/PrimeRatePage';
import { PrimeRatesPage } from '/imports/ui/PrimeRatesPage';
import { CeaAffiliateIndicativeEssSolarProposalPage } from '/imports/ui/CeaAffiliateIndicativeEssSolarProposalPage';
import { CeaAffiliateIndicativeEssProposalPage } from '/imports/ui/CeaAffiliateIndicativeEssProposalPage';
import { CeaAffiliateDetailedEssSolarProposalPage } from '/imports/ui/CeaAffiliateDetailedEssSolarProposalPage';
import { CeaAffiliateDetailedEssProposalPage } from '/imports/ui/CeaAffiliateDetailedEssProposalPage';
import { CeaDealerIndicativeEssSolarProposalPage } from '/imports/ui/CeaDealerIndicativeEssSolarProposalPage';
import { CeaDealerIndicativeEssProposalPage } from '/imports/ui/CeaDealerIndicativeEssProposalPage';
import { CeaDealerDetailedEssSolarProposalPage } from '/imports/ui/CeaDealerDetailedEssSolarProposalPage';
import { CeaDealerDetailedEssProposalPage } from '/imports/ui/CeaDealerDetailedEssProposalPage';
import { SettingsPage } from '/imports/ui/SettingsPage';
import { RolesPage } from '/imports/ui/RolesPage';
import { ProspectBatchesPage } from '/imports/ui/ProspectBatchesPage';
import { ProspectBatchPage } from '/imports/ui/ProspectBatchPage';
import { EnphaseRedirectPage } from '/imports/ui/EnphaseRedirectPage';
import { SmaMeterPage } from '/imports/ui/SmaMeterPage';
import { SmaClientMeterPage } from '/imports/ui/SmaClientMeterPage';
import { SmaRedirectPage } from '/imports/ui/SmaRedirectPage';
import { SolarBillPage } from '/imports/ui/SolarBillPage';
import { NorthshoreBillsPage } from '/imports/ui/NorthshoreBillsPage';
import { CalpineBillsPage } from '/imports/ui/CalpineBillsPage';
import { CalpineListPage } from '/imports/ui/CalpineListPage';
import { SolarBillViewPage } from '/imports/ui/SolarBillViewPage';
import { ViewSolarBillsPage } from '/imports/ui/ViewSolarBillsPage';
import { AlsoEnergyMeterPage } from '/imports/ui/AlsoEnergyMeterPage';
import { SolarEdgeMeterPage } from '/imports/ui/SolarEdgeMeterPage';
import { SolisMeterPage } from '/imports/ui/SolisMeterPage';
import { UtilityAccountIntakePage } from '/imports/ui/UtilityAccountIntakePage';
import { ConsumptionMetersPage } from '/imports/ui/ConsumptionMetersPage';
import { ConsumptionMeterPage } from '/imports/ui/ConsumptionMeterPage';
import { UtilityAccountPasswordPage } from '/imports/ui/UtilityAccountPasswordPage';
import { UtilityAccountsPage } from '/imports/ui/UtilityAccountsPage';
import { UtilityAccountPage } from '/imports/ui/UtilityAccountPage';
import { RicDashboardPage25Year, RicDashboardPageQuarterly, RicDashboardPageYtd } from '/imports/ui/RicDashboardPage';
import { UtilityAccountIntakeLink } from '/imports/ui/UtilityAccountIntakeLink';
import { EnergyExportPage } from '/imports/ui/EnergyExportPage';
import { TestResultsPage } from '/imports/ui/TestResultsPage';
// Project Management module (prototype) — remove these imports + the route block below to back out
import { ProjectManagementPipelinePage } from '/imports/ui/ProjectManagementPipelinePage';
import { ProjectManagementProjectPage } from '/imports/ui/ProjectManagementProjectPage';
import { InventoryDashboardPage } from '/imports/ui/InventoryDashboardPage';
import { InventoryPalletsPage } from '/imports/ui/InventoryPalletsPage';
import { InventorySitesPage, InventorySitePage } from '/imports/ui/InventorySitesPage';
import { InventoryDeliveriesPage } from '/imports/ui/InventoryDeliveriesPage';
import { InventoryLotsPage, InventoryLotPage } from '/imports/ui/InventoryLotsPage';
import { InventoryWarehousesPage, InventoryWarehousePage } from '/imports/ui/InventoryWarehousesPage';
import { InventoryShipmentsPage, InventorySerialsPage } from '/imports/ui/InventoryShipmentsPage';
import { InventoryImportPage } from '/imports/ui/InventoryImportPage';
import { DocumentsPage } from '/imports/ui/DocumentsPage';
import { SharedDocumentPage } from '/imports/ui/SharedDocumentPage';
import { ProjectManagementPortfoliosPage } from '/imports/ui/ProjectManagementPortfoliosPage';
import { ProjectManagementTeamPage } from '/imports/ui/ProjectManagementTeamPage';
import { ProjectManagementInvoicesPage } from '/imports/ui/ProjectManagementInvoicesPage';
import { ProjectManagementSettingsPage } from '/imports/ui/ProjectManagementSettingsPage';
import { ProjectManagementBulkEditPage } from '/imports/ui/ProjectManagementBulkEditPage';
import { ProjectManagementPortfolioPage } from '/imports/ui/ProjectManagementPortfolioPage';
import { ProjectManagementOwnerPage, ProjectManagementEpcPage, ProjectManagementUtilityPage } from '/imports/ui/ProjectManagementPartyPage';
import { ProjectManagementOwnersPage, ProjectManagementEpcsPage, ProjectManagementUtilitiesPage } from '/imports/ui/ProjectManagementPartiesPage';

/** Root application component: routing, auth gating, and org/theme setup. */
export const App = () => {
  const pathname = window.location.pathname;
  const publicPath = isPublicPath();
  const loggingIn = useLoggingIn();
  const user = useUser();
  const userId = user?._id;
  const orgId = user?.orgId;
  const isLoadingOrgs = useSubscribe(Publications.listMyOrgs);
  registerSubscription(Publications.listMyOrgs, isLoadingOrgs);
  const loadingOrgs = isLoadingOrgs();
  const account = useFindOne(() => Accounts.find({ userId, orgId }), [`${userId}_${orgId}`]);
  useLoggedOutRedirect();
  const theme = { main: user?.settings?.theme || 'light' };
  verificationToastEffect(user, loadingOrgs, pathname);
  const isUnreadMessagesLoading = useSubscribe(Publications.unreadMessageReceipts);
  registerSubscription(Publications.unreadMessageReceipts, isUnreadMessagesLoading);
  const loading = loggingIn || loadingOrgs;
  if (loading && !publicPath) return <LoadingFullScreen />;
  const query = queryStringToObject();
  if (shouldReloadForOrg(user, query.redirectOrgId)) {
    setOrgId.call(query.redirectOrgId);
    setTimeout(() => window.location.reload());
    return <LoadingFullScreen />;
  }
  if (publicPath) {
    return (
      <ThemeProvider theme={theme}>
        <AppStyled>
          <Router>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup/:code" element={<SignupPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/signup-submitted" element={<SignupSubmittedPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/quickbooks/connected" element={<QuickbooksConnectedPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/oauth2-complete" element={<Oauth2ThankYouPage />} />
              <Route path="/google-oauth2-redirect" element={<GoogleOauth2RedirectPage />} />
              <Route path="/discord-oauth2-redirect" element={<DiscordOauth2RedirectPage />} />
              <Route path="/cea-affiliate-detailed-ess-solar-proposal" element={<CeaAffiliateDetailedEssSolarProposalPage />} />
              <Route path="/cea-affiliate-detailed-ess-proposal" element={<CeaAffiliateDetailedEssProposalPage />} />
              <Route path="/cea-affiliate-indicative-ess-solar-proposal" element={<CeaAffiliateIndicativeEssSolarProposalPage />} />
              <Route path="/cea-affiliate-indicative-ess-proposal" element={<CeaAffiliateIndicativeEssProposalPage />} />
              <Route path="/cea-dealer-indicative-ess-solar-proposal" element={<CeaDealerIndicativeEssSolarProposalPage />} />
              <Route path="/cea-dealer-indicative-ess-proposal" element={<CeaDealerIndicativeEssProposalPage />} />
              <Route path="/cea-dealer-detailed-ess-solar-proposal" element={<CeaDealerDetailedEssSolarProposalPage />} />
              <Route path="/cea-dealer-detailed-ess-proposal" element={<CeaDealerDetailedEssProposalPage />} />
              <Route path="/solar-bill-view" element={<SolarBillViewPage />} />
              <Route path="/view-solar-bills" element={<ViewSolarBillsPage />} />
              <Route path="/shared" element={<SharedDocumentPage />} />
              <Route path="/enphase-redirect" element={<EnphaseRedirectPage />} />
              <Route path="/sma-redirect" element={<SmaRedirectPage />} />
              <Route path="/utility-account-intake" element={<UtilityAccountIntakePage />} />
              <Route path="/uail" element={<UtilityAccountIntakeLink />} />
              <Route path="/uai" element={<UtilityAccountIntakePage />} />
              <Route path="/r" element={<RedirectionPage />} />
              <Route path="/" element={<LandingRedirect user={user} account={account} ready={!loadingOrgs} />} />
            </Routes>
          </Router>
          <ToastContainer position="top-right" />
        </AppStyled>
      </ThemeProvider>
    );
  }
  return (
    <ThemeProvider theme={theme}>
      <AppStyled>
        <Router>
          <Nav account={account} items={[
            // { path: '/scan', text: strings.Scan(), icon: 'qrCodeScanner' }
          ]} />
          <MainContent>
            <AppHeader />
            <Routes>
              {CommonRoutes('')}
              <Route path="/projects/:_id/view" element={<ProjectViewPage />} />
              <Route path="/projects/:_id" element={<ProjectPage />} />
              <Route path="/resigen" element={<ResiGenPage />} />
              <Route path="/resigen/:_id/view" element={<ResiGenProjectViewPage />} />
              <Route path="/projects" element={<ProjectsPage />} />
              <Route path="/production-meters/egauge" element={<EgaugeMeterPage />} />
              <Route path="/production-meters/enphase" element={<EnphaseMeterPage />} />
              <Route path="/production-meters/powerhub" element={<PowerhubMeterPage />} />
              <Route path="/production-meters/fomware" element={<FomwareMeterPage />} />
              <Route path="/production-meters/sma" element={<SmaMeterPage />} />
              <Route path="/production-meters/sma-client" element={<SmaClientMeterPage />} />
              <Route path="/production-meters/alsoenergy" element={<AlsoEnergyMeterPage />} />
              <Route path="/production-meters/solarEdge" element={<SolarEdgeMeterPage />} />
              <Route path="/production-meters/solis" element={<SolisMeterPage />} />
              <Route path="/production-meter-report" element={<ProductionMeterReportPage />} />
              <Route path="/production-meters/:_id" element={<ProductionMeterPage />} />
              <Route path="/production-meters" element={<ProductionMetersPage />} />
              <Route path="/consumption-meters/:_id" element={<ConsumptionMeterPage />} />
              <Route path="/consumption-meters" element={<ConsumptionMetersPage />} />
              <Route path="/energy-export" element={<EnergyExportPage />} />
              <Route path="/test-results" element={<TestResultsPage />} />
              <Route path="/utility-accounts/:_id" element={<UtilityAccountPage />} />
              <Route path="/utility-accounts" element={<UtilityAccountsPage />} />
              <Route path="/solar-bills/:_id" element={<SolarBillPage />} />
              <Route path="/solar-bills/dollar-general" element={<RealtyIncomeBillsPage />} />
              <Route path="/solar-bills/tractor-supply" element={<RealtyIncomeBillsPage />} />
              <Route path="/solar-bills/ulta-beauty" element={<RealtyIncomeBillsPage />} />
              <Route path="/solar-bills/northshore" element={<NorthshoreBillsPage />} />
              <Route path="/solar-bills/calpine" element={<CalpineBillsPage />} />
              <Route path="/calpine/list" element={<CalpineListPage />} />
              <Route path="/solar-bills/new" element={<NewSolarBillsPage />} />
              <Route path="/solar-bills" element={<SolarBillsPage />} />
              <Route path="/prime-rates/:_id" element={<PrimeRatePage />} />
              <Route path="/prime-rates" element={<PrimeRatesPage />} />
              <Route path="/utility-accounts/:_id/password" element={<UtilityAccountPasswordPage />} />
              <Route path="/ric-dashboard/ytd" element={<RicDashboardPageYtd />} />
              <Route path="/ric-dashboard/quarterly" element={<RicDashboardPageQuarterly />} />
              <Route path="/ric-dashboard/25-year" element={<RicDashboardPage25Year />} />
              <Route path="/messages" element={<MessagesPage />} />
              <Route path="/environment" element={<EnvironmentPage />} />
              <Route path="/devtools" element={<DevToolsPage />} />
              <Route path="/prospector/:_id" element={<ProspectBatchPage />} />
              <Route path="/prospector" element={<ProspectBatchesPage />} />
              {/* Project Management module (prototype) — remove this block to back out */}
              <Route path="/project-management/projects/:_id" element={<ProjectManagementProjectPage />} />
              <Route path="/project-management/bulk/:mode" element={<ProjectManagementBulkEditPage />} />
              <Route path="/project-management/bulk" element={<ProjectManagementBulkEditPage />} />
              <Route path="/project-management/portfolios/:_id" element={<ProjectManagementPortfolioPage />} />
              <Route path="/project-management/portfolios" element={<ProjectManagementPortfoliosPage />} />
              <Route path="/project-management/team" element={<ProjectManagementTeamPage />} />
              <Route path="/project-management/invoices" element={<ProjectManagementInvoicesPage />} />
              <Route path="/project-management/settings" element={<ProjectManagementSettingsPage />} />
              <Route path="/project-management/owners/:_id" element={<ProjectManagementOwnerPage />} />
              <Route path="/project-management/owners" element={<ProjectManagementOwnersPage />} />
              <Route path="/project-management/epcs/:_id" element={<ProjectManagementEpcPage />} />
              <Route path="/project-management/epcs" element={<ProjectManagementEpcsPage />} />
              <Route path="/project-management/utilities/:_id" element={<ProjectManagementUtilityPage />} />
              <Route path="/project-management/utilities" element={<ProjectManagementUtilitiesPage />} />
              <Route path="/project-management/pipeline" element={<Navigate to="/project-management" replace />} />
              <Route path="/inventory/pallet-inventory/:_id" element={<InventoryPalletsPage />} />
              <Route path="/inventory/pallet-inventory" element={<InventoryPalletsPage />} />
              <Route path="/inventory/sites/:_id" element={<InventorySitePage />} />
              <Route path="/inventory/sites" element={<InventorySitesPage />} />
              <Route path="/inventory/deliveries" element={<InventoryDeliveriesPage />} />
              <Route path="/inventory/lots/:_id" element={<InventoryLotPage />} />
              <Route path="/inventory/lots" element={<InventoryLotsPage />} />
              <Route path="/inventory/warehouses/:_id" element={<InventoryWarehousePage />} />
              <Route path="/inventory/warehouses" element={<InventoryWarehousesPage />} />
              <Route path="/inventory/shipments" element={<InventoryShipmentsPage />} />
              <Route path="/inventory/serials" element={<InventorySerialsPage />} />
              <Route path="/inventory/import" element={<InventoryImportPage />} />
              <Route path="/inventory" element={<InventoryDashboardPage />} />
              <Route path="/documents/:folderId" element={<DocumentsPage />} />
              <Route path="/documents" element={<DocumentsPage />} />
              <Route path="/project-management" element={<ProjectManagementPipelinePage />} />
              <Route path="/manage" element={<ManagePage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/roles" element={<RolesPage />} />
              <Route path="/r" element={<RedirectionPage />} />
              <Route path="/" element={<LandingRedirect user={user} account={account} ready={!loadingOrgs} />} />
            </Routes>
            {isMobile() && <FooterNav />}
          </MainContent>
        </Router>
        <ImpersonationBanner />
        <ToastContainer position="top-center" />
      </AppStyled>
    </ThemeProvider>
  );
};

/**
 * `/` is not a page: once the signed-in user's account is known it forwards to their landing page
 * (last main-Nav page, else a role-based default — see landingPathFor). Signed-out visitors just wait
 * here for the login redirect.
 */
const LandingRedirect = ({ user, account, ready }: { user?: Meteor.User, account?: Account, ready: boolean }) => {
  if (!user || !ready) return <LoadingFullScreen />;
  return <Navigate to={landingPathFor(user, account)} replace />;
};

/**
 * Shows a toast prompting unverified users to resend email verification.
 * @param user - current user, if any
 * @param loading - whether org data is still loading
 * @param pathname - current route path
 */
const verificationToastEffect = (user: Meteor.User | undefined, loading: boolean, pathname: string) => {
  return;
  const resend = async () => {
    try {
      await sendEmailVerification.call();
      toast.success(strings.Sent());
    }
    catch (error) {
      console.log(error);
      toast.error(`${error}`);
    }
  };
  useEffect(() => {
    setTimeout(async () => {
      if (user?.isSuperUser) return;
      if (!user || loading || pathname?.endsWith('/verify-email')) return;
      if (user && !user?.verified && !loading) toast.info(<span onClick={() => resend()}>{strings.VerificationRequiredMessage()}</span>, { autoClose: false });
    }, 3000);
  }, [`${user?._id}_${loading}`]);
};

/**
 * Whether the app should switch orgs and reload for a redirect-requested org.
 * @param user - current user
 * @param orgId - org id requested via query string
 * @returns true if a reload into the requested org is warranted
 */
const shouldReloadForOrg = (user?: Meteor.User, orgId?: string) => {
  return orgId && user?.orgId !== orgId && (user?.isSuperUser || Orgs.find().map((o) => o._id).includes(orgId));
};